import { cn } from '@forest/shared'
import * as DialogPrimitives from '@kobalte/core/dialog'
import { onCleanup, Show } from 'solid-js'
import { Spinner } from '~/components/spinner'
import {
  closeDialog,
  dialog,
  flushDialogAfterExitAction,
  queueDialogAfterExitAction,
  setDialogLoading,
} from '~/store/dialog'

/** Mounted inside the dialog content so its cleanup marks the moment the
 *  content unmounts (exit animation done, body-style locks released). */
function FlushAfterExitOnUnmount() {
  onCleanup(() => flushDialogAfterExitAction())
  return null
}

export function Dialog() {
  const handlePrimary = () => {
    if (dialog.asyncPrimaryAction) {
      setDialogLoading(true)
      // asyncPrimaryAction owns dialog state: it must close, transition to a
      // follow-up dialog, or otherwise resolve the loading state itself.
      dialog.asyncPrimaryAction().finally(() => {
        setDialogLoading(false)
      })
    } else {
      dialog.primaryAction?.()
      if (dialog.primaryActionAfterExit) {
        queueDialogAfterExitAction(dialog.primaryActionAfterExit)
      }
      closeDialog()
    }
  }

  const handleSecondary = () => {
    dialog.secondaryAction?.()
    closeDialog()
  }

  return (
    <DialogPrimitives.Dialog
      open={dialog.isOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen && !dialog.isLoading) closeDialog()
      }}
    >
      <DialogPrimitives.Portal>
        <DialogPrimitives.Overlay class="fixed inset-0 z-[100] backdrop-blur-[8px] data-[expanded]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[expanded]:fade-in-0" />
        <DialogPrimitives.Content
          class="backdrop-filter-[50px] fixed left-1/2 top-1/2 z-[100] w-[calc(100vw-48px)] max-w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-[32px] border-2 border-white/30 bg-white/75 p-6 data-[expanded]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[expanded]:fade-in-0"
          style={{
            'will-change': 'backdrop-filter',
            ['--tw-enter-translate-x' as never]: '-50%',
            ['--tw-enter-translate-y' as never]: '-50%',
            ['--tw-exit-translate-x' as never]: '-50%',
            ['--tw-exit-translate-y' as never]: '-50%',
          }}
          onInteractOutside={(e) => {
            e.preventDefault()
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault()
          }}
        >
          <FlushAfterExitOnUnmount />
          <div class="flex flex-col gap-6">
            <div class="flex flex-col gap-2 text-center">
              <DialogPrimitives.Title class="text-lg font-semibold text-[#333333]">
                {dialog.title}
              </DialogPrimitives.Title>
              <p
                class={cn(
                  'whitespace-pre-line break-words text-base text-[#666666]',
                  dialog.descriptionClass,
                )}
              >
                {typeof dialog.description === 'function'
                  ? dialog.description()
                  : dialog.description}
              </p>
            </div>

            <Show
              when={dialog.secondaryButtonText}
              fallback={
                <button
                  type="button"
                  class="flex h-[52px] w-full items-center justify-center rounded-xl bg-[#176361] text-base font-bold text-white active:bg-[#0f4d4b] disabled:opacity-70"
                  disabled={dialog.isLoading}
                  onClick={handlePrimary}
                >
                  <Show
                    when={!dialog.isLoading}
                    fallback={
                      <Spinner variant="circular" class="size-6 text-white" />
                    }
                  >
                    {dialog.primaryButtonText}
                  </Show>
                </button>
              }
            >
              <div
                class={cn(
                  'gap-2',
                  dialog.buttonLayout === 'stacked'
                    ? 'flex flex-col'
                    : 'grid grid-cols-2',
                )}
              >
                <button
                  type="button"
                  class={cn(
                    'flex h-[52px] items-center justify-center rounded-xl text-base font-bold text-[#176361]',
                    dialog.buttonLayout === 'stacked' && 'order-2 w-full',
                  )}
                  disabled={dialog.isLoading}
                  onClick={handleSecondary}
                >
                  {dialog.secondaryButtonText}
                </button>
                <button
                  type="button"
                  class="flex h-[52px] items-center justify-center rounded-xl bg-[#176361] text-base font-bold text-white active:bg-[#0f4d4b] disabled:opacity-70"
                  disabled={dialog.isLoading}
                  onClick={handlePrimary}
                >
                  <Show
                    when={!dialog.isLoading}
                    fallback={
                      <Spinner variant="circular" class="size-6 text-white" />
                    }
                  >
                    {dialog.primaryButtonText}
                  </Show>
                </button>
              </div>
            </Show>
          </div>
        </DialogPrimitives.Content>
      </DialogPrimitives.Portal>
    </DialogPrimitives.Dialog>
  )
}
